'use client';
// Revue de direction (design isRdd — ISO 15189 §8.9) : entrées de revue
// normées, décisions → plan d'actions, compte-rendu imprimable signé.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { LigneLien, enTeteCollant } from '@/shared/Tableau';
import { cheminDe } from '@/lib/adresse';
import { useGed, userName } from '@/lib/store';
import { RDD_STATES, vocabMeta, fmtDate } from '@/lib/meta';
import {
  TitrePage, I, card, inputStyle,
  Skeleton, EmptyState, FieldError, ConfirmButton, useBusy, RefISO, FilterBar, MentionTri, Selecteur } from '@/components/ui';
import { BoutonJournal } from '@/components/BoutonJournal';
import type { GedRdd, GedRevueDossier, GedRddParametrage, GedRddEntreeCatalogue } from '@/lib/types';
import { useTri, BoutonsTri } from '@/shared/Tri';
import { Norme } from '@/components/Norme';
// GED-11 / GED-12 — la planification vit dans SON composant, appelé aussi par
// le calendrier du tableau de bord. Une seule création, atteignable de deux
// endroits : voir l'en-tête de `components/PlanifierRevue.tsx`.
import { PlanifierRevue, formaterDuree } from '@/components/PlanifierRevue';

import { Bouton } from '@/shared/Bouton';
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 };

/** Paramétrage des entrées de revue (GED-11).
 *
 *  ── Pourquoi ce panneau est ICI et non dans « Paramétrage » ─────────────────
 *
 *  Décision du 21/07 : le paramétrage se fait PAR MODULE, au plus près de
 *  l'écran qu'il règle. Une RQ qui prépare une revue n'a pas à traverser
 *  l'application pour choisir son ordre du jour, et un réglage exilé dans un
 *  centre unifié n'est jamais relu.
 *
 *  ── Ce que le panneau doit rendre visible, et qu'il ne suffit pas de refuser ─
 *
 *  Les entrées exigées par la norme sont VERROUILLÉES, avec leur clause en
 *  clair. Le serveur refuse aussi — un écran ne garde rien — mais laisser
 *  découvrir l'interdit après le clic reviendrait à s'en remettre à la mémoire
 *  de la RQ, ce que ce lot cherche justement à ne plus faire.
 *
 *  Et chaque entrée dit si elle est ALIMENTÉE par le dossier consolidé (lot C7)
 *  ou si elle est à documenter à la main. C'est ce raccordement qui fait qu'on
 *  arrive en revue avec des chiffres et non avec un ordre du jour ; une entrée
 *  qui n'est qu'un titre libre doit s'annoncer comme telle. */
function ParametrageEntrees({ onFerme }: { onFerme: () => void }) {
  const { showToast, session, settings } = useGed();
  const [param, setParam] = useState<GedRddParametrage | null>(null);
  const [items, setItems] = useState<{ cle: string; libelle: string; actif: boolean; dureeMin: number }[]>([]);
  const [erreur, setErreur] = useState('');
  const [nouvelle, setNouvelle] = useState('');
  const { run, estOccupe } = useBusy();
  const isRQ = session.roles.includes('rq');

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/rdd-parametrage', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: GedRddParametrage | null) => {
        if (!vivant) return;
        setParam(j);
        setItems(j ? j.items.map((i) => ({ ...i })) : []);
      })
      .catch(() => { if (vivant) setParam(null); });
    return () => { vivant = false; };
  }, []);

  const parCle = useMemo(() => {
    const m = new Map<string, GedRddEntreeCatalogue>();
    (param?.catalogue || []).forEach((c) => m.set(c.cle, c));
    return m;
  }, [param]);

  const actifs = items.filter((i) => i.actif);
  const total = actifs.reduce((n, i) => n + (i.dureeMin || 0), 0);
  const majItem = (cle: string, patch: Partial<{ actif: boolean; dureeMin: number; libelle: string }>) => {
    setItems((prev) => prev.map((i) => (i.cle === cle ? { ...i, ...patch } : i)));
    if (erreur) setErreur('');
  };

  /* L'entrée libre se construit à un seul endroit : « Ajouter » et « Enregistrer »
     passent par là, sans quoi l'une des deux dériverait de l'autre. */
  const entreeLibre = (saisie: string, existantes: typeof items): { item: (typeof items)[number] } | { erreur: string } => {
    const titre = saisie.trim();
    // La clé d'une entrée libre est dérivée du titre, préfixée `libre:` — c'est
    // le préfixe qui dit au serveur qu'elle n'est raccordée à aucun chiffre.
    const base = `libre:${titre.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50)}`;
    if (base === 'libre:') return { erreur: 'Ce titre ne produit aucune clé exploitable.' };
    let cle = base; let n = 2;
    while (existantes.some((i) => i.cle === cle)) { cle = `${base}-${n}`; n += 1; }
    return { item: { cle, libelle: titre.slice(0, 160), actif: true, dureeMin: 15 } };
  };

  const ajouterLibre = () => {
    if (!nouvelle.trim()) return;
    const e = entreeLibre(nouvelle, items);
    if ('erreur' in e) { setErreur(e.erreur); return; }
    setItems([...items, e.item]);
    setNouvelle('');
  };

  const enregistrer = () => run('save', async () => {
    setErreur('');
    /* ⚠️ L'ENTRÉE EN COURS DE SAISIE PART AVEC LES AUTRES (09/10/2026). On tape
       un intitulé, on clique « Enregistrer » sans passer par « Ajouter » : la
       page se refermait (`onFerme`) et l'entrée disparaissait sans un mot. */
    let aEnvoyer = items;
    if (nouvelle.trim()) {
      const e = entreeLibre(nouvelle, items);
      if ('erreur' in e) { setErreur(e.erreur); return; }
      aEnvoyer = [...items, e.item];
    }
    const rep = await fetchEcriture('/api/ged/ged/rdd-parametrage', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: aEnvoyer }),
    });
    if (!rep.ok) {
      const j = await rep.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Paramétrage refusé.');
      return;
    }
    showToast(`Entrées de revue enregistrées — ${actifs.length} retenue(s), ${formaterDuree(total)}.`);
    onFerme();
  });

  return (
    <div style={{ ...card, padding: 18, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>Entrées de revue — paramétrage</h2>
        <span style={{ flex: 1 }} />
        {param?.regle
          ? <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Réglé par {param.majPar ? userName(settings, param.majPar) : '—'} le {fmtDate(param.majLe)}</span>
          : <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Jamais réglé — valeurs proposées</span>}
      </div>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '90ch' }}>
        Ce réglage vaut pour les revues <strong>à venir</strong>. Les revues déjà planifiées gardent
        l’ordre du jour figé le jour de leur planification : le modifier ici réécrirait un compte
        rendu qui ne correspondrait plus à la séance tenue.
      </p>

      {!param && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture du paramétrage…</div>}

      {param && (
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {items.map((it, i) => {
            const cat = parCle.get(it.cle);
            const normatif = !!cat?.normatif;
            const alimente = !!cat?.rubrique;
            const libre = it.cle.startsWith('libre:');
            return (
              <div key={it.cle} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', background: it.actif ? 'transparent' : 'var(--surface-sunken)' }}>
                <input type="checkbox" checked={it.actif} disabled={normatif || !isRQ}
                  aria-label={`Retenir « ${it.libelle} »`}
                  onChange={(e) => majItem(it.cle, { actif: e.target.checked })}
                  style={{ accentColor: 'var(--color-primary)', cursor: normatif ? 'not-allowed' : 'pointer' }} />
                <input value={it.libelle} disabled={!isRQ}
                  aria-label={`Intitulé de l’entrée « ${it.cle} »`}
                  onChange={(e) => majItem(it.cle, { libelle: e.target.value })}
                  style={{ ...inputStyle, height: 30, flex: '1 1 300px', fontSize: 12.5 }} />
                {normatif && (
                  <span title={`Entrée exigée par la norme (${cat?.clause}) : elle ne peut pas être retirée de la revue de direction.`}
                    style={{ fontSize: 10, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', background: 'var(--navy-800)', color: 'var(--text-on-primary)', cursor: 'help', whiteSpace: 'nowrap' }}>
                    exigé {cat?.clause}
                  </span>
                )}
                <span title={alimente
                  ? 'Cette entrée arrive en séance AVEC ses chiffres, extraits des enregistrements de la période (dossier de revue, lot C7).'
                  : 'Aucun chiffre n’est extrait pour cette entrée : elle se documente à la main en séance.'}
                  style={{ fontSize: 10, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 8px', cursor: 'help', whiteSpace: 'nowrap', background: alimente ? 'var(--green-50)' : 'var(--gray-100)', color: alimente ? 'var(--green-600)' : 'var(--text-muted)' }}>
                  {alimente ? 'chiffres extraits' : libre ? 'entrée libre — à la main' : 'à remplir à la main'}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" min={0} max={600} step={5} value={it.dureeMin} disabled={!it.actif || !isRQ}
                    aria-label={`Durée de « ${it.libelle} » en minutes`}
                    onChange={(e) => majItem(it.cle, { dureeMin: Math.max(0, Number(e.target.value) || 0) })}
                    style={{ ...inputStyle, height: 30, width: 76, fontSize: 12.5, textAlign: 'right' }} />
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>min</span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {param && isRQ && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={nouvelle} onChange={(e) => setNouvelle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouterLibre(); } }}
            placeholder="Ajouter une entrée propre au laboratoire…"
            aria-label="Intitulé de la nouvelle entrée de revue"
            style={{ ...inputStyle, height: 32, flex: '1 1 280px', fontSize: 12.5 }} />
          <Bouton taille="sm" onClick={ajouterLibre}><I n="plus" s={13} />Ajouter</Bouton>
        </div>
      )}

      <FieldError>{erreur}</FieldError>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          {actifs.length} entrée{actifs.length > 1 ? 's' : ''} retenue{actifs.length > 1 ? 's' : ''} · durée cumulée {formaterDuree(total)}
        </span>
        <span style={{ flex: 1 }} />
        {isRQ && (
          <Bouton variante="primaire" onClick={() => void enregistrer()} disabled={estOccupe() || !param}>
            <I n="check" s={14} />{estOccupe('save') ? 'Enregistrement…' : 'Enregistrer'}
          </Bouton>
        )}
        <Bouton onClick={onFerme}>Fermer</Bouton>
      </div>
    </div>
  );
}

/** Note d'entrée de revue : enregistrée sur `onBlur`, donc silencieusement.
 *  Le marqueur inline dit ce qui est parti — le message global est inadapté à
 *  une sauvegarde champ par champ. */
function NoteEntree({ rddId, index, note }: { rddId: string; index: number; note: string }) {
  const { smq } = useGed();
  const [valeur, setValeur] = useState(note);
  const [etat, setEtat] = useState<'repos' | 'envoi' | 'ok' | 'echec'>('repos');
  // Resynchronisation PENDANT LE RENDU, pas dans un effet : la note peut être
  // réécrite depuis le dossier de revue (bouton « Reprendre »), et un effet qui
  // appelle setState déclenche un rendu en cascade (React 19).
  //
  // On ne remet à zéro que si la valeur venue du serveur diffère de ce qu'on a
  // sous les yeux — sinon le retour de notre propre enregistrement effacerait
  // le « Enregistré ✓ » qu'il vient de justifier.
  const [noteVue, setNoteVue] = useState(note);
  if (note !== noteVue) {
    setNoteVue(note);
    if (note !== valeur) { setValeur(note); setEtat('repos'); }
  }

  const enregistrer = async () => {
    if (valeur === note) return;
    setEtat('envoi');
    const ok = await smq(`ged/rdd/${rddId}/entree`, { index, note: valeur });
    setEtat(ok ? 'ok' : 'echec');
  };

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input value={valeur} onChange={(e) => { setValeur(e.target.value); if (etat !== 'repos') setEtat('repos'); }}
        onBlur={() => void enregistrer()}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        placeholder="Constats / notes…" style={{ ...inputStyle, height: 32, fontSize: 12.5 }} />
      <span role="status" style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', minWidth: 82, color: etat === 'echec' ? 'var(--red-600)' : etat === 'ok' ? 'var(--green-600)' : 'var(--text-muted)' }}>
        {etat === 'envoi' ? 'Enregistrement…' : etat === 'ok' ? 'Enregistré ✓' : etat === 'echec' ? 'Échec ✕' : ''}
      </span>
    </span>
  );
}


/** Dossier de revue : les chiffres de la période, calculés côté serveur.
 *
 *  La revue de direction existait comme réunion — huit entrées et une zone de
 *  note. Les notes se tapaient de mémoire, ou en ouvrant huit écrans à côté.
 *  Ce bloc apporte ce qui manquait : les enregistrements, chiffrés.
 *
 *  Ce qu'il ne fait PAS : écrire dans les notes à la place du RQ. Le bouton
 *  « Reprendre » propose une phrase, il ne la valide pas — une revue de
 *  direction est un acte de direction, pas un rapport automatique. */
function DossierRevue({ r, isRQ, d, onRecalcul }: {
  r: GedRdd; isRQ: boolean; d: GedRevueDossier | null; onRecalcul: () => void;
}) {
  const { smq } = useGed();
  const [ouvert, setOuvert] = useState(true);
  const [periode, setPeriode] = useState({ debut: r.periodeDebut || '', fin: r.periodeFin || '' });
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();

  const bornerPeriode = () => run('per', async () => {
    setErreur('');
    const rep = await fetchEcriture(`/api/ged/ged/rdd/${r.documentId}/periode`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ periodeDebut: periode.debut, periodeFin: periode.fin }),
    });
    if (!rep.ok) {
      const j = await rep.json().catch(() => ({}));
      setErreur(j?.error?.message || 'Période refusée.');
      return;
    }
    onRecalcul();
  });

  if (!d) {
    return <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Constitution du dossier de revue…</div>;
  }

  const modifiable = isRQ && r.statut !== 'cloturee';

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      <button onClick={() => setOuvert(!ouvert)} aria-expanded={ouvert}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', background: 'var(--surface-sunken)', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Dossier de revue</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          période du {fmtDate(d.periode.debut)} au {fmtDate(d.periode.fin)}
        </span>
        {d.alertes > 0 && (
          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--amber-100)', color: 'var(--text-strong)' }}>
            {d.alertes} point{d.alertes > 1 ? 's' : ''} d’attention
          </span>
        )}
        <span style={{ flex: 1 }} />
        <I n={ouvert ? 'check' : 'diff'} s={13} />
      </button>

      {ouvert && (
        <div style={{ padding: 14, display: 'grid', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '84ch' }}>
            Ces chiffres sont lus dans les enregistrements de la période. Ils sont <strong>proposés</strong> :
            c’est vous qui retenez ce qui entre au compte-rendu.
          </p>

          {modifiable && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={lbl} htmlFor={`pd-${r.documentId}`}>Période — début</label>
                <input id={`pd-${r.documentId}`} type="date" value={periode.debut}
                  onChange={(e) => setPeriode({ ...periode, debut: e.target.value })}
                  style={{ ...inputStyle, height: 32, width: 'auto', fontSize: 12.5 }} />
              </div>
              <div>
                <label style={lbl} htmlFor={`pf-${r.documentId}`}>Fin</label>
                <input id={`pf-${r.documentId}`} type="date" value={periode.fin}
                  onChange={(e) => setPeriode({ ...periode, fin: e.target.value })}
                  style={{ ...inputStyle, height: 32, width: 'auto', fontSize: 12.5 }} />
              </div>
              <Bouton taille="sm" onClick={() => void bornerPeriode()} disabled={estOccupe()}>
                {estOccupe('per') ? 'Recalcul…' : 'Recalculer le dossier'}
              </Bouton>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                À défaut, les douze mois précédant la séance.
              </span>
            </div>
          )}
          <FieldError>{erreur}</FieldError>

          {d.rubriques.map((ru) => (
            <div key={ru.cle} style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{ru.titre}</span>
                {ru.entreeIndex === null && (
                  <span title="Cette rubrique n’a pas d’entrée correspondante dans cette revue : elle a été ajoutée après sa planification."
                    style={{ fontSize: 12.5, color: 'var(--text-muted)', cursor: 'help' }}>hors entrées de cette revue</span>
                )}
                <span style={{ flex: 1 }} />
                {ru.note && ru.entreeIndex !== null && r.statut === 'tenue' && isRQ && (
                  <Bouton taille="sm" onClick={() => void run(`n-${ru.cle}`, () =>
                    smq(`ged/rdd/${r.documentId}/entree`, { index: ru.entreeIndex, note: ru.note }, 'Constat repris dans l’entrée.'))} disabled={estOccupe()}>
                    Reprendre dans la note
                  </Bouton>
                )}
              </div>
              {ru.sansSource && (
                <div style={{ fontSize: 12.5, color: 'var(--text-body)', background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '7px 11px', marginBottom: ru.lignes.length ? 8 : 0, lineHeight: 1.5 }}>
                  {ru.sansSource}
                </div>
              )}
              <div style={{ display: 'grid', gap: 2 }}>
                {ru.lignes.map((l, i) => (
                  <div key={i} title={l.aide || undefined}
                    style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12.5, padding: '2px 0', cursor: l.aide ? 'help' : 'default' }}>
                    <span style={{ color: 'var(--text-body)' }}>{l.label}</span>
                    <span style={{ flex: 1, borderBottom: '1px dotted var(--border-subtle)', minWidth: 12 }} />
                    <span style={{ fontWeight: l.alerte ? 700 : 600, color: l.alerte ? 'var(--red-600)' : 'var(--text-strong)', fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                      {l.valeur}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Detail({ r }: { r: GedRdd }) {
  const { smq, settings, session, ouvrirPanneau, actions } = useGed();
  const isRQ = session.roles.includes('rq');
  const [dec, setDec] = useState({ description: '', responsable: '', echeance: '', creerAction: true });
  const [erreurDec, setErreurDec] = useState('');
  const { run, estOccupe } = useBusy();
  const [dossier, setDossier] = useState<GedRevueDossier | null>(null);
  const [tour, setTour] = useState(0);
  const frozen = r.statut === 'cloturee';
  const entrees = r.entrees || [];
  // Durée de l'ordre du jour : somme des durées FIGÉES à la planification.
  // Vaut 0 sur les revues antérieures à GED-11, qui n'en portent aucune — et
  // c'est alors le total qui disparaît, pas un « 0 min » qui mentirait.
  const dureeOrdreDuJour = entrees.reduce((n, e) => n + (Number(e.dureeMin) || 0), 0);

  // `.then()` et non `await` dans l'effet : une fonction `async` appelée
  // directement masque le fait que les `setState` suivent le premier await.
  useEffect(() => {
    let vivant = true;
    void fetch(`/api/ged/ged/rdd/${r.documentId}/dossier`, { credentials: 'include' })
      .then((rep) => (rep.ok ? rep.json() : null))
      .then((j) => { if (vivant) setDossier(j); })
      .catch(() => { if (vivant) setDossier(null); });
    return () => { vivant = false; };
  }, [r.documentId, tour]);
  const decisions = r.decisions || [];
  const gedUsers = settings?.gedUsers || [];

  const ajouterDecision = () => run('dec', async () => {
    if (!dec.description.trim()) { setErreurDec('Décrivez la décision avant de l’ajouter.'); return; }
    setErreurDec('');
    const ok = await smq(`ged/rdd/${r.documentId}/decision`, dec, dec.creerAction ? 'Décision enregistrée — action créée au plan.' : 'Décision enregistrée.');
    if (ok) setDec({ description: '', responsable: '', echeance: '', creerAction: true });
  });

  return (
    <div style={{ ...card, padding: 20, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{r.ref}</span>
        <BoutonJournal reference={r.ref} quoi="de cette revue de direction" />
        <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: vocabMeta(RDD_STATES, r.statut).bg, color: vocabMeta(RDD_STATES, r.statut).fg, fontSize: 11, fontWeight: 700 }}>{vocabMeta(RDD_STATES, r.statut).label}</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Revue du {fmtDate(r.dateRevue)}</span>
        <span style={{ flex: 1 }} />
        <Bouton onClick={() => window.print()}><I n="print" s={14} />Compte-rendu (PDF)</Bouton>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
        Participants : {(r.participants || []).map((p) => userName(settings, p)).join(' · ') || '—'}
      </div>

      {isRQ && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {r.statut === 'planifiee' && (
            <Bouton variante="primaire" taille="sm" onClick={() => void run('tenue', () => smq(`ged/rdd/${r.documentId}/status`, { statut: 'tenue' }, 'Revue ouverte — les entrées sont à la saisie.'))} disabled={estOccupe()}>
              {estOccupe('tenue') ? 'En cours…' : 'Tenir la revue'}
            </Bouton>
          )}
          {r.statut === 'tenue' && (
            <ConfirmButton
              label="Clôturer la revue" icon="shield" variant="primary"
              question={`Clôturer ${r.ref} ? Le compte-rendu sera figé et signé : ni les entrées ni les décisions ne pourront plus être modifiées.`}
              confirmer="Clôturer et figer"
              onConfirm={() => smq(`ged/rdd/${r.documentId}/status`, { statut: 'cloturee' }, 'Revue clôturée — compte-rendu figé et signé.')} />
          )}
          {frozen && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--green-600)' }}><I n="check" s={14} />Compte-rendu figé</span>}
        </div>
      )}

      <DossierRevue r={r} isRQ={isRQ} d={dossier} onRecalcul={() => { setDossier(null); setTour((t) => t + 1); }} />

      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', margin: '0 0 8px' }}>
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Entrées de revue ({entrees.length})</h3>
          <span style={{ flex: 1 }} />
          {dureeOrdreDuJour > 0 && (
            <span title="Durées arrêtées à la planification, cumulées. Elles ne changent pas si le paramétrage est modifié ensuite."
              style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', cursor: 'help' }}>
              Durée prévue : {formaterDuree(dureeOrdreDuJour)}
            </span>
          )}
        </div>
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {entrees.map((e, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 12, padding: '9px 14px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', alignItems: 'center' }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                {e.label}
                {typeof e.dureeMin === 'number' && (
                  <span style={{ fontWeight: 500, color: 'var(--text-muted)', fontSize: 12.5 }}> · {formaterDuree(e.dureeMin)}</span>
                )}
                {e.clause && (
                  <span title={`Entrée exigée par la norme (${e.clause}).`} style={{ fontWeight: 500, color: 'var(--text-faint)', fontSize: 12.5, cursor: 'help' }}> {e.clause}</span>
                )}
              </span>
              {r.statut === 'tenue' && isRQ ? (
                <NoteEntree rddId={r.documentId} index={i} note={e.note} />
              ) : (
                <span style={{ fontSize: 12.5, color: e.note ? 'var(--text-body)' : 'var(--text-faint)' }}>{e.note || '—'}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Décisions & sorties ({decisions.length})</h3>
        {decisions.map((d) => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px dashed var(--border-subtle)', flexWrap: 'wrap' }}>
            <span style={{ flex: '1 1 280px', fontSize: 13, color: 'var(--text-body)' }}>{d.description}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{userName(settings, d.responsable)} · {fmtDate(d.echeance)}</span>
            {d.actionRef && (
              <Bouton variante="primaire" taille="sm" onClick={() => {
                const a = actions.find((x) => x.ref === d.actionRef);
                // L'action DÉCOULE de la décision qu'on lit : elle s'ouvre
                // par-dessus la revue, qu'on ne quitte pas en séance.
                if (a) ouvrirPanneau({ type: 'action', valeur: a.documentId, origine: r.ref });
              }} style={{ fontFamily: 'var(--font-mono)' }}>{d.actionRef} →</Bouton>
            )}
          </div>
        ))}
        {decisions.length === 0 && <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Aucune décision enregistrée.</p>}
        {r.statut === 'tenue' && isRQ && (
          <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-md)', padding: 12, marginTop: 10, display: 'grid', gap: 8 }}>
            <input value={dec.description} onChange={(e) => { setDec({ ...dec, description: e.target.value }); if (erreurDec) setErreurDec(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && dec.description.trim()) { e.preventDefault(); void ajouterDecision(); } }}
              placeholder="Nouvelle décision…" aria-invalid={!!erreurDec}
              style={{ ...inputStyle, height: 36, fontSize: 13, borderColor: erreurDec ? 'var(--red-600)' : undefined }} />
            <FieldError>{erreurDec}</FieldError>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Selecteur value={dec.responsable} onChange={(v) => setDec({ ...dec, responsable: v })} aria-label="Responsable de la décision" style={{ ...inputStyle, height: 34, width: 'auto', minWidth: 170, fontSize: 12.5 }}>
                <option value="">Responsable…</option>
                {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
              </Selecteur>
              <input type="date" value={dec.echeance} onChange={(e) => setDec({ ...dec, echeance: e.target.value })} aria-label="Échéance de la décision" style={{ ...inputStyle, height: 34, width: 'auto', fontSize: 12.5 }} />
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-body)', cursor: 'pointer' }}>
                <input type="checkbox" checked={dec.creerAction} onChange={(e) => setDec({ ...dec, creerAction: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
                → créer l’action au plan
              </label>
              <Bouton variante="primaire" taille="sm" onClick={() => void ajouterDecision()} disabled={estOccupe()}>
                <I n="plus" s={13} />{estOccupe('dec') ? 'Ajout…' : 'Ajouter'}
              </Bouton>
            </div>
          </div>
        )}
      </div>

      {/* CR imprimable */}
      <div id="ged-print-area" style={{ display: 'none' }}>
        <div style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-strong)' }}>
          <div style={{ borderBottom: '2px solid var(--navy-900)', paddingBottom: 8, marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{settings?.branding?.labName || 'BIO24 Alliance'} — Compte-rendu de revue de direction</div>
            <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)' }}>{r.ref} · {fmtDate(r.dateRevue)} · {settings?.branding?.norme || ''}</div>
          </div>
          <p style={{ fontSize: 12.5 }}>Participants : {(r.participants || []).map((p) => userName(settings, p)).join(', ')}</p>
          <h2 style={{ fontSize: 14 }}>
            Entrées passées en revue
            {dureeOrdreDuJour > 0 && <span style={{ fontWeight: 400, fontSize: 12.5 }}> — durée prévue {formaterDuree(dureeOrdreDuJour)}</span>}
          </h2>
          <table className="ged-tableau" ref={enTeteCollant} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead><tr><th scope="col" style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'left' }}>Élément d’entrée</th><th scope="col" style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'left' }}>Réf.</th><th scope="col" style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Durée</th><th scope="col" style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'left' }}>Constats / notes</th></tr></thead>
            <tbody>{entrees.map((e, i) => (
              <tr key={i}>
                <td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{e.label}</td>
                <td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{e.clause || '—'}</td>
                <td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'center' }}>{typeof e.dureeMin === 'number' ? formaterDuree(e.dureeMin) : '—'}</td>
                <td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{e.note || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
          {dossier && (
            <>
              <h2 style={{ fontSize: 14, marginTop: 14 }}>
                Dossier de revue — période du {fmtDate(dossier.periode.debut)} au {fmtDate(dossier.periode.fin)}
              </h2>
              {dossier.rubriques.map((ru) => (
                <div key={ru.cle} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 12.5 }}>{ru.titre}</div>
                  {ru.sansSource && <div style={{ fontSize: 12.5, fontStyle: 'italic' }}>{ru.sansSource}</div>}
                  {ru.lignes.length > 0 && (
                    <table ref={enTeteCollant} className="ged-tableau" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <tbody>
                        {ru.lignes.map((l, i) => (
                          <tr key={i}>
                            <td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{l.label}</td>
                            <td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'right', fontWeight: l.alerte ? 700 : 400 }}>{l.valeur}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </>
          )}
          <h2 style={{ fontSize: 14, marginTop: 14 }}>Décisions & actions décidées</h2>
          <table className="ged-tableau" ref={enTeteCollant} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead><tr><th scope="col" style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>#</th><th scope="col" style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'left' }}>Décision</th><th scope="col" style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'left' }}>Responsable</th><th scope="col" style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Échéance</th><th scope="col" style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>Action</th></tr></thead>
            <tbody>{decisions.map((d, i) => <tr key={d.id}><td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'center' }}>{i + 1}</td><td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{d.description}</td><td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>{userName(settings, d.responsable)}</td><td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'center' }}>{fmtDate(d.echeance)}</td><td style={{ border: '1px solid var(--gray-400)', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{d.actionRef || '—'}</td></tr>)}</tbody>
          </table>
          <div style={{ display: 'flex', gap: 60, marginTop: 40, fontSize: 12.5 }}>
            <div>Le Responsable Qualité<br /><br /><br />______________________</div>
            <div>La Direction<br /><br /><br />______________________</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* L8 (11/10/2026) : tri du socle (`useTri`) ; la plus récente en tête reste
   l'ordre par défaut, dit par `MentionTri`. */
const VALEURS_TRI_RDD: Record<string, (r: GedRdd) => string> = {
  ref: (r) => r.ref,
  dateRevue: (r) => r.dateRevue || '',
  statut: (r) => r.statut,
};

export function Rdd() {
  const { rdds, selectedRdd, select, session, loading, booted } = useGed();
  const isRQ = session.roles.includes('rq');
  const [planifie, setPlanifie] = useState(false);
  const [parametre, setParametre] = useState(false);
  const sel = rdds.find((r) => r.documentId === selectedRdd) || null;
  const enChargement = !booted || (loading && rdds.length === 0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('toutes');

  // Une revue se cherche par son année ou par une décision qu'on sait y avoir
  // été prise — d'où la recherche sur les décisions, pas seulement sur la
  // référence : « qu'avait-on décidé sur la métrologie ? » est la question
  // qu'on pose vraiment à ce registre.
  const enCours = rdds.filter((r) => r.statut !== 'cloturee');
  const closes = rdds.filter((r) => r.statut === 'cloturee');
  const liste = useMemo(() => {
    const base = filtre === 'encours' ? enCours : filtre === 'closes' ? closes : rdds;
    const qq = q.trim().toLowerCase();
    const cherche = qq
      ? base.filter((r) => `${r.ref} ${r.dateRevue} ${(r.decisions || []).map((d) => d.description).join(' ')}`.toLowerCase().includes(qq))
      : base;
    // La plus récente en tête : c'est celle qu'on vient consulter.
    return [...cherche].sort((a, b) => (b.dateRevue || '').localeCompare(a.dateRevue || ''));
  }, [rdds, q, filtre, enCours, closes]);
  const { tri, basculer: onTri, trier } = useTri<GedRdd>(null, VALEURS_TRI_RDD);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div className="no-print">
        <TitrePage eyebrow={<>Pilotage · <RefISO code="8.9" /><Norme art="8.9" /></>} titre="Revue de direction"
          actions={<>
            {/* Ce qui est tracé se VOIT (24/08/2026) : le paramétrage des
                entrées s'écrivait sous « REVUE-DIRECTION-PARAM » sans accès. */}
            <BoutonJournal reference="REVUE-DIRECTION-PARAM" quoi="du paramétrage des revues de direction" />
            {isRQ && !parametre && (
              <Bouton onClick={() => { setParametre(true); setPlanifie(false); }} title="Choisir les entrées de revue et leur durée. Les revues déjà planifiées ne changent pas.">
                <I n="gear" s={15} />Entrées de revue
              </Bouton>
            )}
            {isRQ && !planifie && (
              <Bouton variante="primaire" onClick={() => { setPlanifie(true); setParametre(false); }}>
                <I n="plus" s={15} />Planifier une revue
              </Bouton>
            )}
          </>} />
      </div>

      {isRQ && parametre && <div className="no-print"><ParametrageEntrees onFerme={() => setParametre(false)} /></div>}
      {isRQ && planifie && <div className="no-print"><PlanifierRevue onFerme={() => setPlanifie(false)} /></div>}

      <div className="ged-2col-wide ged-colonnes-defilantes no-print-grid" style={{ display: 'grid', gridTemplateColumns: '320px minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
        <div className="no-print">
          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher une revue ou une décision…"
            chips={[
              { k: 'toutes', label: 'Toutes', n: rdds.length },
              { k: 'encours', label: 'En cours', n: enCours.length },
              { k: 'closes', label: 'Clôturées', n: closes.length },
            ]}
            actif={filtre} onChip={setFiltre}
            total={rdds.length} affiches={liste.length}
            onReset={() => { setQ(''); setFiltre('toutes'); }} />
          <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <MentionTri>de la revue la plus récente à la plus ancienne.</MentionTri>
            <span style={{ flex: 1 }} />
            <BoutonsTri label="Trier les revues de direction" tri={tri} onTri={onTri}
              options={[{ cle: 'ref', libelle: 'Référence' }, { cle: 'dateRevue', libelle: 'Date de revue' }, { cle: 'statut', libelle: 'Statut' }]} />
          </div>
        <div style={{ ...card, overflow: 'hidden' }}>
          {enChargement ? <Skeleton rows={4} height={56} /> : trier(liste).map((r) => (
            <LigneLien key={r.documentId} href={cheminDe('rdd', r.ref)}
              title={`Ouvrir ${r.ref}`}
              onOuvrir={() => select('selectedRdd', r.documentId)}
              style={{ width: '100%', textAlign: 'start', background: selectedRdd === r.documentId ? 'var(--cyan-50)' : 'none', borderTop: '1px solid var(--border-subtle)', padding: '12px 14px', fontFamily: 'var(--font-sans)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{r.ref}</span>
                <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: vocabMeta(RDD_STATES, r.statut).bg, color: vocabMeta(RDD_STATES, r.statut).fg, fontSize: 11, fontWeight: 700 }}>{vocabMeta(RDD_STATES, r.statut).label}</span>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>{fmtDate(r.dateRevue)} · {(r.decisions || []).length} décision(s)</div>
            </LigneLien>
          ))}
          {!enChargement && liste.length === 0 && (
            <EmptyState
              titre={rdds.length === 0 ? 'Aucune revue planifiée.' : 'Aucune revue ne correspond à ces critères.'}
              indice={rdds.length === 0
                ? (isRQ ? 'Planifiez la première revue : les entrées normées §8.9 seront créées automatiquement.' : 'Le responsable qualité planifie les revues de direction.')
                : 'La recherche porte aussi sur le texte des décisions.'}
              action={rdds.length > 0
                ? <Bouton taille="sm" onClick={() => { setQ(''); setFiltre('toutes'); }}>Réinitialiser les filtres</Bouton>
                : undefined} />
          )}
        </div>
        </div>
        {sel ? <Detail r={sel} /> : (
          <div className="no-print" style={card}>
            <EmptyState titre="Compte-rendu de revue" indice="Sélectionnez une revue pour la conduire ou l’imprimer." />
          </div>
        )}
      </div>
    </div>
  );
}
